'use client';

import {
  useContext,
  useCallback,
  useMemo,
  useEffect,
  useState,
  FC,
} from 'react';
import {
  Button,
  ButtonGroup,
  Text,
  Checkbox,
  DraggableQuestionnaireItems,
  Flex,
  Dialog,
  Input,
  InputChips,
  LargeBulkSendConfirmationDialog,
  Select,
  EntitySelectorInputChips,
  SelectItem,
  Heading,
  ResultListItem,
  Tooltip,
} from '@/_components';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import type {
  PriorYearItem,
  PriorYearItem as PriorYearItemModel,
} from '@/_types/models/priorYearItem';
import type { QuestionnaireTemplate } from '@/_types/questionnaireTemplates';
import {
  sendQuestionnaires,
  type SendQuestionnaireParams,
} from '@/_api/admin-questionnaires';
import {
  addPriorYearItem,
  getPriorYearItems,
} from '@/_api/admin-prior-year-items';
import { TAX_YEARS_WITHOUT_DEFAULT } from '@/_constants/year-options';
import { TEMPLATE_FREQUENCY_OPTIONS } from '@/_constants/questionnaireTemplates';
import { validateDueDate } from '@/_utils/validations';
import { RItem } from './components/RItem';
import {
  getNDaysAfter,
  getInputFormattedDateForNDaysFromNow,
  getIsPastDate,
} from '@/_utils/dates';
import { SendTemplateDialogProps } from './SendTemplateDialog.types';
import { CompanyContext } from '@/_contexts/admin/CompanyProvider';
import { useEntityCount } from '@/_hooks/useEntityCount';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { NotificationsContext } from '@/_contexts/admin/NotificationsProvider';
import { QuestionnaireContext } from '@/_contexts/admin/QuestionnaireProvider';
import { QuestionnaireTemplateContext } from '@/_contexts/admin/QuestionnaireTemplateProvider';
import { useItemList } from '@/_hooks/useItemList';
import { ADDITIONAL_SECTION } from '@/_constants/sections';
import { SELECTABLE_CLIENT_ROLES } from '@/_types/models/questionnaire';

const TODAYS_DATE: string = getNDaysAfter(0);
const AFTER_7_DAYS: string = getNDaysAfter(7);
const AFTER_14_DAYS: string = getNDaysAfter(14);
const AFTER_30_DAYS: string = getNDaysAfter(30);

export const SendTemplateDialog: FC<SendTemplateDialogProps> = ({
  initiallyCheckScheduledSend,
  initiallySelectedCompanyIds = [],
  isOpen,
  onClose,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(
    initiallyCheckScheduledSend || false,
  );
  const [isScheduledSendChecked, setIsScheduledSendChecked] =
    useState<boolean>(false);
  const [isAddingNewPriorYearItem, setIsAddingNewPriorYearItem] =
    useState<boolean>(false);
  const [newPriorYearItemName, setNewPriorYearItemName] = useState<string>('');
  const [customReceiverClientRole, setCustomReceiverClientRole] =
    useState<string>(SELECTABLE_CLIENT_ROLES[0].value);
  const [sectionsFilter, setSectionsFilter] = useState<string>('');
  const [selectedSections, setSelectedSections] = useState<Set<string>>(
    new Set(),
  );
  const [frequency, setFrequency] = useState<string | null>(null);
  const [questionnaireTemplateId, setQuestionnaireTemplateId] =
    useState<string>('');
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<Array<number>>(
    initiallySelectedCompanyIds,
  );
  const [taxYear, setTaxYear] = useState<string>('');

  const [daysUntilDue, setDaysUntilDue] = useState<number>(0);
  const [firstScheduledDate, setFirstScheduledDate] = useState<string>(
    getInputFormattedDateForNDaysFromNow(1),
  );
  const [includePriorYearItems, setIncludePriorYearItems] =
    useState<boolean>(false);
  const [allOwnerSignatureRequired, setAllOwnerSignatureRequired] =
    useState<boolean>(false);
  const [depositRequired, setDepositRequired] = useState<boolean>(false);
  const [depositAmount, setDepositAmount] = useState<number>(0);
  const [updateProfileSection, setUpdateProfileSection] =
    useState<boolean>(false);
  const [batchSending, setBatchSending] = useState<boolean>(false);
  const [priorYearItems, setPriorYearItems] = useState<PriorYearItemModel[]>(
    [],
  );
  const [includeAdditionalQuestions, setIncludeAdditionalQuestions] =
    useState<boolean>(false);
  const [bulkSelectedEntityTypes, setBulkSelectedEntityTypes] = useState<
    Array<string>
  >([]);

  const {
    items,
    addItem,
    removeItem,
    updateItem,
    reorderItems,
    resetItems,
    isEmpty,
  } = useItemList();

  const onAdditionalQuestionsToggle = () => {
    const isTogglingToOn = !includeAdditionalQuestions;
    setIncludeAdditionalQuestions(isTogglingToOn);

    // Reset to initial item if toggling to on and no additional questions added
    if (isTogglingToOn && items.length === 0) {
      resetItems();
    }
  };

  const [dueDate, setDueDate] = useState<string>('');
  const [
    shouldShowLargeBulkSendConfirmation,
    setShouldShowLargeBulkSendConfirmation,
  ] = useState<boolean>(false);
  const [shouldSendDraft, setShouldSendDraft] = useState<boolean>(false);
  const { addToast } = useContext(ToastContext);
  const { token } = useContext(UserContext);
  const { selectedCompany, refreshCompanyQuestionnaires } =
    useContext(CompanyContext);
  const { isStripeConnected, preferences } = useContext(FirmSettingsContext);
  const { reloadNotifications } = useContext(NotificationsContext);
  const { loadQuestionnaires, loadQuestionnaireBacklogCount } =
    useContext(QuestionnaireContext);
  const {
    isLoading: isLoadingQuestionnaireTemplates,
    loadQuestionnaireTemplates,
    questionnaireTemplates,
  } = useContext(QuestionnaireTemplateContext);

  useEffect(() => {
    setIsLoading(isLoadingQuestionnaireTemplates);
  }, [isLoadingQuestionnaireTemplates]);

  useEffect(() => {
    if (initiallyCheckScheduledSend) {
      setIsScheduledSendChecked(true);

      if (!frequency) setFrequency('once');
      if (!daysUntilDue) setDaysUntilDue(14);
    }
  }, [daysUntilDue, frequency, initiallyCheckScheduledSend]);

  const initialSelectedCompanyIds: Array<number> = useMemo(
    () =>
      !!selectedCompanyIds?.length
        ? selectedCompanyIds
        : selectedCompany?.id
          ? [selectedCompany?.id]
          : [],
    [selectedCompany?.id, selectedCompanyIds],
  );

  useEffect(() => {
    if (!isOpen) {
      onReset();
    } else {
      if (initialSelectedCompanyIds?.length) {
        onChipsChange(initialSelectedCompanyIds);
      }
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset everything to initial state
  // NOTE @chris this is necessary as we never unmount this component
  const onReset = useCallback(() => {
    setIsLoading(false);
    setIsScheduledSendChecked(false);
    setIsAddingNewPriorYearItem(false);
    setNewPriorYearItemName('');
    setSectionsFilter('');
    setSelectedSections(new Set());
    setFrequency(null);
    setQuestionnaireTemplateId('');
    setSelectedCompanyIds(
      initiallySelectedCompanyIds?.length ? initiallySelectedCompanyIds : [],
    );
    setTaxYear('');
    setDueDate(AFTER_14_DAYS);
    setDaysUntilDue(0);
    setFirstScheduledDate(getInputFormattedDateForNDaysFromNow(1));
    setIncludePriorYearItems(false);
    setAllOwnerSignatureRequired(false);
    setDepositRequired(false);
    setDepositAmount(0);
    setUpdateProfileSection(false);
    setBatchSending(false);
    setPriorYearItems([]);
    setIncludeAdditionalQuestions(false);
    resetItems();
    setBulkSelectedEntityTypes([]);
    setCustomReceiverClientRole('use_default');

    onClose();
  }, [initiallySelectedCompanyIds, resetItems, onClose]);

  useEffect(() => {
    if (!!initiallySelectedCompanyIds?.length) {
      setSelectedCompanyIds(initiallySelectedCompanyIds);
    }
  }, [initiallySelectedCompanyIds]);

  const isDueDateValid: boolean = !!dueDate
    ? getIsPastDate(TODAYS_DATE, dueDate)
    : true;

  const hasCompanySelected: boolean =
    !!selectedCompanyIds?.length || !!bulkSelectedEntityTypes?.length;

  const isValid: boolean =
    !!questionnaireTemplateId &&
    hasCompanySelected &&
    !!taxYear &&
    isDueDateValid &&
    (includeAdditionalQuestions
      ? !isEmpty && items?.every((q) => q.text)
      : true);

  // Load templates
  useEffect(() => {
    loadQuestionnaireTemplates();
  }, [loadQuestionnaireTemplates]);

  // Load prior year items
  useEffect(() => {
    const fetchPriorYearItemsData = async () => {
      try {
        setIsLoading(true);
        const priorYearItems = (await getPriorYearItems({
          companyId: selectedCompanyIds[0]!,
          token,
        })) as any;

        setPriorYearItems(priorYearItems);
      } catch (error) {
        addToast('error', null, { error });
      } finally {
        setIsLoading(false);
      }
    };

    includePriorYearItems && selectedCompanyIds[0] && fetchPriorYearItemsData();
  }, [includePriorYearItems, addToast, selectedCompanyIds, token]);

  // Selected QuestionnaireTemplate
  const questionnaireTemplate: QuestionnaireTemplate | undefined = useMemo(
    () =>
      questionnaireTemplates?.length
        ? questionnaireTemplates?.find(
            (template) => template?.id === Number(questionnaireTemplateId),
          )
        : undefined,
    [questionnaireTemplates, questionnaireTemplateId],
  );

  const questionnaireTemplatesAsItems: Array<SelectItem> = useMemo(() => {
    const templates: Array<SelectItem> =
      questionnaireTemplates?.map((template) => ({
        value: template.id.toString(),
        name: template.name || template.id.toString(),
      })) || [];

    return [{ name: 'Select Template', value: '' }, ...templates];
  }, [questionnaireTemplates]);

  // Update available sections when selected QuestionnaireTemplate changes
  useEffect(() => {
    if (questionnaireTemplate) {
      const selectedSectionNames: Set<string> = new Set(
        questionnaireTemplate.sections.map((s) => s.name),
      );

      setSelectedSections(selectedSectionNames);
    }
  }, [questionnaireTemplate]);

  const isTaxYearSetByFrequency =
    isScheduledSendChecked && frequency && frequency !== 'once';

  const entityCountForConfirmation = useEntityCount({
    bulkSelectedEntityTypes,
    selectedCompanyIds,
  });

  const onSubmit = (isDraft: boolean) => {
    if (!questionnaireTemplateId) {
      addToast('error', 'Please select a template');
      return;
    }

    if (!taxYear) {
      addToast('error', 'Please select a tax year');
      return;
    }

    if (!hasCompanySelected) {
      addToast('error', 'Please select a company');
      return;
    }

    if (!validateDueDate(dueDate)) {
      addToast('error', 'Please select a valid due date');
      return;
    }

    if (isScheduledSendChecked && !frequency) {
      addToast('error', 'Please select a frequency');
      return;
    }

    if (isScheduledSendChecked && !firstScheduledDate) {
      addToast('error', 'Please select a send date');
      return;
    }

    if (depositRequired && !depositAmount) {
      addToast('error', 'Please enter a deposit amount');
      return;
    }

    if (depositRequired && !depositAmount && !isStripeConnected) {
      addToast('error', 'Please connect Stripe to Soraban first');
      return;
    }

    setShouldSendDraft(isDraft);

    if (selectedCompanyIds.length >= 10 || !!bulkSelectedEntityTypes?.length) {
      setShouldShowLargeBulkSendConfirmation(true);
    } else {
      performQuestionnaireSend(isDraft);
    }
  };

  const performQuestionnaireSend = async (isDraft: boolean) => {
    const questionsToCreate = items.map((item) => {
      const isUpload = item.question_type === 'upload';

      return {
        options: isUpload ? [item.text] : [],
        question_type: item.question_type,
        text:
          isUpload && !preferences.remove_please_upload_for_rollover_items
            ? `Please upload ${item.text}`
            : item.text,
        section_id: item.section_id,
      };
    });

    // TODO(API): @chris what happens if sections has the same name?
    const params: SendQuestionnaireParams = {
      sections: Array.from(selectedSections),
      frequency: isScheduledSendChecked ? frequency : null,
      questionnaire_template_id: Number(questionnaireTemplateId),
      company_ids: selectedCompanyIds,
      company_types: bulkSelectedEntityTypes,
      tax_year: isTaxYearSetByFrequency
        ? new Date(firstScheduledDate).getFullYear()
        : Number(taxYear),
      due_date: !isScheduledSendChecked ? dueDate : null,
      days_until_due: isScheduledSendChecked ? daysUntilDue : null,
      first_scheduled_date: firstScheduledDate,
      include_prior_year_items: includePriorYearItems,
      show_uploads_as_document_checklist: false,
      all_owner_signature_required: allOwnerSignatureRequired,
      deposit_required: depositRequired,
      deposit_amount: depositAmount,
      update_profile_section: updateProfileSection, // Request Profile Review
      batch_sending: batchSending, // Send all questionnaires in one email
      batch_reminder: batchSending,
      document_popout_checklist_enabled: false,
      is_draft: isDraft,
      questions_to_create: includeAdditionalQuestions ? questionsToCreate : [],
      custom_receiver_client_role:
        customReceiverClientRole === 'use_default'
          ? undefined
          : customReceiverClientRole,
    };

    try {
      setIsLoading(true);
      const { message } = await sendQuestionnaires({ params, token });

      if (!!message) {
        const isPlural: boolean = selectedCompanyIds?.length > 1;
        const successMessage: string = isDraft
          ? `Successfully saved Questionnaire draft${isPlural ? 's' : ''}`
          : message;

        addToast('success', successMessage);
      }
    } catch (error) {
      addToast('error', '', { error });
    } finally {
      setIsLoading(false);
      reloadNotifications(true, 3000);
      onReset();

      setTimeout(() => {
        loadQuestionnaireBacklogCount();
      }, 100);

      setTimeout(async () => {
        await loadQuestionnaires();
      }, 5000);

      if (selectedCompany?.id) {
        refreshCompanyQuestionnaires(selectedCompany?.id, true);
      }
    }
  };

  const onSavePriorYearItem = async () => {
    if (!newPriorYearItemName) {
      addToast('error', 'Please enter a name');
      return;
    }

    if (!taxYear) {
      addToast('error', 'Please select a tax year');
      return;
    }

    const companyId: number = selectedCompanyIds[0];
    const params = {
      name: newPriorYearItemName,
      companyId,
      year: taxYear,
    };

    try {
      setIsLoading(true);
      const response = (await addPriorYearItem({ ...params, token })) as any;

      if (response.error) {
        throw new Error(response.exception);
      }

      const priorYearItemsFromResponse = (await getPriorYearItems({
        companyId,
        token,
      })) as any;

      setPriorYearItems(priorYearItemsFromResponse);
      addToast('success', 'Prior year item added');
      setNewPriorYearItemName('');
      setIsAddingNewPriorYearItem(false);
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setIsLoading(false);
    }
  };

  const onChipsChange = async (chips: Array<number>) => {
    setSelectedCompanyIds(chips);
  };

  const onYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const year = e.target.value.trim();
    setTaxYear(year);
  };

  const onQuestionnaireChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const templateId = e.target.value.trim();

    setQuestionnaireTemplateId(templateId);
  };

  const onSectionsFilter = (e: React.ChangeEvent<HTMLInputElement>) =>
    setSectionsFilter(e.target.value);

  const sectionResults: Array<ResultListItem> =
    questionnaireTemplate?.sections?.map((s) => ({
      name: s.name,
      value: s.name,
    })) || [];

  const onDepositAmountChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setDepositAmount(Number(e.target.value));

  const onAfter7Days = () => setDueDate(AFTER_7_DAYS);
  const onAfter14Days = () => setDueDate(AFTER_14_DAYS);
  const onAfter30Days = () => setDueDate(AFTER_30_DAYS);

  const filteredPriorYearItems: Array<PriorYearItem> = useMemo(
    () => priorYearItems?.filter((item) => item.year === Number(taxYear)) || [],
    [priorYearItems, taxYear],
  );

  const availableSections: Array<SelectItem> = useMemo(() => {
    return [
      ADDITIONAL_SECTION,
      ...(questionnaireTemplate?.sections
        ?.filter((s) => s.section_key === 'questionnaire')
        .map((s) => ({
          name: s.name || '',
          value: s.id.toString() || '',
        })) || []),
    ];
  }, [questionnaireTemplate]);

  return (
    <>
      <Dialog
        isOpen={isOpen}
        onClose={onClose}
        title="Send Questionnaire Template"
      >
        <Dialog.Content>
          <Flex isColumn={true} gap={6}>
            {/* Entities, Templates and Year */}
            <Flex gap={6}>
              <EntitySelectorInputChips
                hideDisabledEntities={true}
                label="Select Entity"
                setBulkSelectedEntityTypes={setBulkSelectedEntityTypes}
                onClearBulkSelection={() => setBulkSelectedEntityTypes([])}
                bulkSelectedEntityTypes={bulkSelectedEntityTypes}
                isBulkTypeSelectionEnabled={!selectedCompany?.id}
                isDisabled={
                  !!selectedCompany?.id || !!bulkSelectedEntityTypes?.length
                }
                preselectedEntityIds={initialSelectedCompanyIds}
                onChipsChange={onChipsChange}
              />
              <Select
                isDisabled={isLoading}
                items={questionnaireTemplatesAsItems}
                label="Template"
                placeholder="Select Template"
                shouldAutoSelectIfSingle={true}
                onChange={onQuestionnaireChange}
                selectedValue={questionnaireTemplateId?.toString()}
              />
              <Select
                isDisabled={isLoading}
                items={TAX_YEARS_WITHOUT_DEFAULT}
                placeholder="Select Tax Year"
                label={
                  isTaxYearSetByFrequency
                    ? 'Tax Year (Year of send date)'
                    : 'Tax Year'
                }
                onChange={onYearChange}
                selectedValue={taxYear}
              />
            </Flex>
            {/* Sections */}
            {questionnaireTemplate?.id && (
              <InputChips
                value={sectionsFilter}
                setChips={setSelectedSections}
                chips={selectedSections}
                hasNoChipLimit={true}
                results={sectionResults}
                label="Sections"
                onChange={onSectionsFilter}
              />
            )}
            {/* When to send */}
            {!isScheduledSendChecked ? (
              <Flex gap={4} isColumn={true}>
                <Input
                  label="Due Date"
                  min={TODAYS_DATE}
                  type="date"
                  isDisabled={isLoading}
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                />
                <ButtonGroup isSmallGroup={true}>
                  <Button
                    value="7 days from now"
                    iconLeft={
                      dueDate === AFTER_7_DAYS
                        ? 'check-circle-filled'
                        : 'circle'
                    }
                    intent="secondary"
                    size="tiny"
                    onClick={onAfter7Days}
                  />
                  <Button
                    value="2 weeks from now"
                    iconLeft={
                      dueDate === AFTER_14_DAYS
                        ? 'check-circle-filled'
                        : 'circle'
                    }
                    intent="secondary"
                    size="tiny"
                    onClick={onAfter14Days}
                  />
                  <Button
                    value="1 month from now"
                    iconLeft={
                      dueDate === AFTER_30_DAYS
                        ? 'check-circle-filled'
                        : 'circle'
                    }
                    intent="secondary"
                    size="tiny"
                    onClick={onAfter30Days}
                  />
                </ButtonGroup>
                <Text type="tiny" color="secondary">
                  We stop sending reminders past the due date.
                  <br />
                  Locking a sent Questionnaire would also prevent recurring
                  reminders.
                </Text>
              </Flex>
            ) : (
              <Flex gap={6}>
                <Input
                  label="Send Date"
                  min={TODAYS_DATE}
                  type="date"
                  value={firstScheduledDate}
                  onChange={(e) => setFirstScheduledDate(e.target.value)}
                />
                <Select
                  items={TEMPLATE_FREQUENCY_OPTIONS.map((frequency) => ({
                    value: frequency.value,
                    name: frequency.text,
                  }))}
                  label="Frequency"
                  onChange={(e) => setFrequency(e.target.value)}
                  selectedValue={frequency?.toString()}
                />
                <Input
                  label="Days Until Due"
                  type="number"
                  min={0}
                  value={daysUntilDue.toString()}
                  onChange={(e) => setDaysUntilDue(Number(e.target.value))}
                />
              </Flex>
            )}
            {depositRequired && (
              <Input
                label="Deposit Amount"
                type="number"
                min={0}
                iconLeft="dollar"
                value={depositAmount.toString()}
                onChange={onDepositAmountChange}
              />
            )}
            <Flex isColumn={true} gap={6}>
              <Heading level={4}>Options</Heading>
              <Flex isColumn={true} gap={2}>
                {isScheduledSendChecked && (
                  <Checkbox
                    isChecked={batchSending}
                    onChange={(e) => setBatchSending(e.target.checked)}
                    label="Send Scheduled Questionnaires in One Email"
                  />
                )}
                <Checkbox
                  isChecked={includePriorYearItems}
                  helperText={
                    Boolean(selectedCompanyIds?.length > 1)
                      ? "You can edit rollover items in each entity's dashboard."
                      : ''
                  }
                  onChange={(e) => setIncludePriorYearItems(e.target.checked)}
                  label="Include Rollover Items"
                />
                <Checkbox
                  label="Include Additional Questions"
                  isChecked={includeAdditionalQuestions}
                  onChange={onAdditionalQuestionsToggle}
                />
                <Checkbox
                  isChecked={allOwnerSignatureRequired}
                  onChange={(e) =>
                    setAllOwnerSignatureRequired(e.target.checked)
                  }
                  label="Get All Users' Signatures"
                />
                {preferences?.billing_enabled &&
                  (!isStripeConnected ? (
                    <Tooltip>
                      <Tooltip.Content>
                        <Text type="small" color="currentcolor">
                          Stripe Integration is required.
                        </Text>
                      </Tooltip.Content>
                      <Tooltip.Trigger>
                        <Checkbox
                          isChecked={depositRequired}
                          isDisabled={true}
                          onChange={(e) => setDepositRequired(e.target.checked)}
                          label="Deposit Required"
                        />
                      </Tooltip.Trigger>
                    </Tooltip>
                  ) : (
                    <Checkbox
                      isChecked={depositRequired}
                      onChange={(e) => setDepositRequired(e.target.checked)}
                      label="Deposit Required"
                    />
                  ))}

                <Checkbox
                  isChecked={updateProfileSection}
                  onChange={(e) => setUpdateProfileSection(e.target.checked)}
                  label="Request Profile Review"
                />
                <Checkbox
                  isChecked={isScheduledSendChecked}
                  onChange={(e) => {
                    setIsScheduledSendChecked(e.target.checked);

                    if (!daysUntilDue) setDaysUntilDue(14);
                    if (!frequency) setFrequency('once');
                  }}
                  label="Scheduled or recurring delivery"
                />
                {preferences?.override_questionnaire_recipient_by_role_enabled &&
                  !isScheduledSendChecked && (
                    <Select
                      items={SELECTABLE_CLIENT_ROLES}
                      label="Override Recipient by Client Role"
                      onChange={(e) =>
                        setCustomReceiverClientRole(e.target.value)
                      }
                      selectedValue={customReceiverClientRole?.toString()}
                    />
                  )}
              </Flex>
            </Flex>
            {includePriorYearItems && selectedCompanyIds.length === 1 && (
              <Flex isColumn={true} gap={6}>
                <Heading level={4}>Rollover Items</Heading>
                <Flex isColumn={true} gap={4}>
                  {filteredPriorYearItems?.map((item) => {
                    return (
                      <RItem
                        setPriorYearItems={setPriorYearItems}
                        item={item}
                        key={item.id}
                        companyId={selectedCompanyIds[0]}
                      />
                    );
                  })}
                </Flex>
                {!isAddingNewPriorYearItem && (
                  <ButtonGroup>
                    <Button
                      onClick={() => setIsAddingNewPriorYearItem(true)}
                      value="Add New Item"
                      intent="secondary"
                      size="small"
                    />
                  </ButtonGroup>
                )}
                {isAddingNewPriorYearItem && (
                  <Flex isBetween={true} gap={6}>
                    <Input
                      label="Rollover Item Name"
                      isLabelHidden={true}
                      onChange={(e) => setNewPriorYearItemName(e.target.value)}
                      type="text"
                      placeholder="Rollover Item Name"
                      value={newPriorYearItemName}
                    />
                    <ButtonGroup isSmallGroup={true} isSameLine={true}>
                      <Button
                        onClick={() => setIsAddingNewPriorYearItem(false)}
                        isDisabled={isLoading}
                        value="Cancel"
                        intent="secondary"
                        size="small"
                      />
                      <Button
                        onClick={onSavePriorYearItem}
                        value="Save"
                        isDisabled={!newPriorYearItemName.trim()}
                        intent="primary"
                        size="small"
                      />
                    </ButtonGroup>
                  </Flex>
                )}
              </Flex>
            )}
            {includeAdditionalQuestions && (
              <DraggableQuestionnaireItems
                items={items}
                updateItem={updateItem}
                removeItem={removeItem}
                addItem={addItem}
                onReorder={reorderItems}
                addButtonText="Add additional item"
                availableSections={availableSections}
              />
            )}
          </Flex>
        </Dialog.Content>
        <Dialog.Footer>
          <Flex isBetween={true}>
            <ButtonGroup>
              <Button value="Cancel" onClick={onClose} intent="secondary" />
            </ButtonGroup>
            <ButtonGroup align="right">
              <Button
                value={
                  selectedCompanyIds?.length > 1 ? 'Save drafts' : 'Save draft'
                }
                intent="secondary"
                onClick={() => onSubmit(true)}
                type="button"
                isDisabled={isLoading || !isValid || isScheduledSendChecked}
                isLoading={isLoading}
                tooltip="Save as draft(s) and send later. Your users will NOT be able to view this yet."
                withTooltip
              />
              <Button
                value={
                  selectedCompanyIds?.length > 1
                    ? 'Send Templates'
                    : 'Send Template'
                }
                intent="primary"
                onClick={() => onSubmit(false)}
                type="button"
                iconRight="send"
                isDisabled={isLoading || !isValid}
                isLoading={isLoading}
              />
            </ButtonGroup>
          </Flex>
        </Dialog.Footer>
      </Dialog>
      {shouldShowLargeBulkSendConfirmation && (
        <LargeBulkSendConfirmationDialog
          isOpen={shouldShowLargeBulkSendConfirmation}
          onClose={() => setShouldShowLargeBulkSendConfirmation(false)}
          entityCount={entityCountForConfirmation}
          bulkSelectedEntityTypes={bulkSelectedEntityTypes}
          onConfirm={() => {
            performQuestionnaireSend(shouldSendDraft);
            setShouldShowLargeBulkSendConfirmation(false);
          }}
          isDraft={shouldSendDraft}
        />
      )}
    </>
  );
};
